/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useEffect } from "react";
import { TextField, RichTextField, RichText } from "@sitecore-jss/sitecore-jss-nextjs";
import Image from "next/image";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { CookieBannerToggleSwitch } from "./CookieBannerToggleSwitch";
import { extractRelativePath } from "components/Shared/DomainSplit";
import IsRtl from "components/Shared/isRtl";
import { DGAButton } from "components/Shared/dga-button/DGAButton";

type CookiesBannerProps = {
  AcceptButtonText?: TextField;
  RejectButtonText?: TextField;
  BannerTitle?: TextField;
  BannerDescription?: RichTextField;
  ManageButtonText?: TextField;
  ManageTitle?: TextField;
  ManageDecription?: any;
  ManageOptions?: any[];
  ConfirmOptionsButtonText?: TextField;
  RejectAllButtonText?: TextField;
  ResponseMessage?: any;
  UndoButtonText?: TextField;
  MainBannerIcon: any;
  closeBanner: () => void;
};

export const CookiesBanner: FunctionComponent<CookiesBannerProps> = ({
  AcceptButtonText,
  BannerDescription,
  BannerTitle,
  ConfirmOptionsButtonText,
  ManageButtonText,
  ManageDecription,
  ManageOptions,
  ManageTitle,
  RejectAllButtonText,
  RejectButtonText,
  ResponseMessage,
  UndoButtonText,
  closeBanner,
  MainBannerIcon,
}) => {
  const [showManageWindow, setShowManageWindow] = React.useState<boolean>(false);
  const [showSuccessWindow, setShowSuccessWindow] = React.useState<boolean>(false);

  const [checkedOptions, setCheckedOptions] = React.useState<
    { id: string; name: string; checked: boolean }[]
  >([]);

  useEffect(() => {
    // set the always checked options to be part of the checked array
    setCheckedOptions(
      ManageOptions?.map((option: any) =>
        option?.fields?.alwaysActive?.value === true
          ? {
              id: option?.id as string,
              name: option?.name as string,
              checked: true,
            }
          : {
              id: option?.id as string,
              name: option?.name as string,
              checked: false,
            }
      ) ?? []
    );
  }, []);

  // Auto-close the success window (undo banner) after 10 seconds if user doesn't interact
  useEffect(() => {
    if (showSuccessWindow) {
      const timer = setTimeout(() => {
        closeAll();
      }, 10000); // 10 seconds

      return () => {
        clearTimeout(timer);
      };
    }
    return undefined;
  }, [showSuccessWindow]);

  const closeAll = () => {
    setShowManageWindow(false);
    setShowSuccessWindow(false);
    closeBanner();
  };
  const saveConsentHttpOnly = async (optionsJson: string) => {
    const res = await fetch("/api/consent/cookies", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ options: optionsJson }),
    });
    if (!res.ok) {
      throw new Error("Failed to save cookie consent");
    }
  };

  const clearConsentHttpOnly = async () => {
    const res = await fetch("/api/consent/cookies", {
      method: "DELETE",
      credentials: "include",
    });
    if (!res.ok) {
      throw new Error("Failed to clear cookie consent");
    }
  };

  const handleAccept = () => {
    void (async () => {
      try {
        await saveConsentHttpOnly(
          JSON.stringify(
            ManageOptions?.map((option: any) => ({
              id: option?.id,
              name: option?.name,
              checked: true,
            }))
          )
        );
        setShowSuccessWindow(true);
      } catch {
        setShowSuccessWindow(true);
      }
    })();
  };
  const handleReject = () => {
    // cookies rejected nothing will happen
    closeAll();
  };
  const handleManage = () => {
    setShowManageWindow(true);
  };
  const handleUndo = () => {
    void (async () => {
      try {
        await clearConsentHttpOnly();
      } catch {
        // still close UI even if API fails
      } finally {
        setShowManageWindow(false);
        setShowSuccessWindow(false);
      }
    })();
  };

  const handleConfirmOptions = () => {
    void (async () => {
      try {
        await saveConsentHttpOnly(JSON.stringify(checkedOptions));
      } catch {
        // still show success UI
      } finally {
        setShowManageWindow(false);
        setShowSuccessWindow(true);
      }
    })();
  };
  const RejectAll = () => {
    setCheckedOptions([]);
  };

  return (
    <div
      className={`fixed top-0 ${
        IsRtl() ? "right-0" : "left-0"
      } !z-[99999] w-screen h-screen bg-[#00000050]`}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeBanner();
        }
      }}
    >
      <div className="shadow-[0_0px_30px_rgba(0,0,0,0.1)] fixed bottom-0 w-full bg-white !z-[9999]">
        {/* content */}
        <div className="responsive py-4 md:py-8 flex flex-col gap-4">
          {/* head */}
          <div className="flex items-center justify-between  ">
            <div className="flex items-center gap-4">
              {MainBannerIcon?.value?.src && (
                <Image
                  unoptimized
                  src={extractRelativePath(MainBannerIcon?.value?.src)}
                  alt="cookies-icon"
                  width={40}
                  height={40}
                  layout="intrinsic"
                />
              )}
              {!showManageWindow ? (
                <div>
                  {BannerTitle?.value && (
                    <h2 className="text-lg font-semibold">{BannerTitle.value}</h2>
                  )}
                </div>
              ) : (
                <div>
                  {ManageTitle?.value && (
                    <h2 className="text-lg font-semibold">{ManageTitle.value}</h2>
                  )}
                </div>
              )}
            </div>
            <DGAButton
              size="iconOnly lg"
              variant="secondarySolid"
              onClick={() => closeAll()}
              aria-label="Close cookies banner"
              buttonContent={<XMarkIcon width={24} height={24} aria-hidden={true} />}
            />
          </div>
          {/* main banner window description */}
          {!showSuccessWindow && !showManageWindow && BannerDescription?.value && (
            <div className="[&_a]:text-primary-800 [&_a:hover]:text-primary-900">
              <RichText field={BannerDescription} />
            </div>
          )}
          {!showSuccessWindow && showManageWindow && ManageDecription?.value && (
            <div className="[&_a]:text-primary-800 [&_a:hover]:text-primary-900">
              <RichText field={ManageDecription} />
            </div>
          )}
          {/* main banner window cta */}
          {!showSuccessWindow && !showManageWindow && (
            <div className="flex flex-col gap-2">
              {/* accept btn */}
              {AcceptButtonText?.value && (
                <DGAButton
                  size="md"
                  variant="primary"
                  className="!w-full"
                  onClick={() => handleAccept()}
                  buttonContent={AcceptButtonText?.value}
                />
              )}
              {/* reject btn */}
              {RejectButtonText?.value && (
                <DGAButton
                  size="md"
                  variant="secondaryOutline"
                  className="!w-full"
                  onClick={() => handleReject()}
                  buttonContent={RejectButtonText?.value}
                />
              )}
              {ManageButtonText?.value && (
                <DGAButton
                  size="md"
                  variant="secondarySolid"
                  className="!w-full"
                  onClick={() => handleManage()}
                  buttonContent={ManageButtonText?.value}
                />
              )}
            </div>
          )}
          {/* manage window content */}
          {showManageWindow && (
            <div>
              {ManageOptions && ManageOptions?.length > 0 && (
                <div className="flex flex-col gap-4">
                  {ManageOptions?.map((option) => {
                    const { Label, AlwaysActive, AlwaysActiveText } = option?.fields || {};
                    return (
                      Label?.value && (
                        <label
                          key={option?.id}
                          htmlFor={option?.id}
                          className="flex gap-2 items-start cursor-pointer w-full"
                        >
                          <CookieBannerToggleSwitch
                            checked={
                              checkedOptions?.find(
                                (item) => item.id === option?.id && item?.checked
                              )
                                ? true
                                : false
                            }
                            onChange={({ id, name, checked }) => {
                              if (checked) {
                                setCheckedOptions((prev) => [
                                  ...prev,
                                  { id, name, checked: checked },
                                ]);
                              } else {
                                setCheckedOptions((prev) => prev.filter((item) => item.id !== id));
                              }
                            }}
                            id={option?.id}
                            name={option?.name}
                            alwaysActive={AlwaysActive?.value}
                          />
                          <div className="flex min-w-0 flex-1 flex-col gap-1">
                            <span className="text-primary-1000 text-sm">{Label?.value}</span>
                            {AlwaysActive?.value && AlwaysActiveText?.value && (
                              <span className="text-neutral-600 text-sm">{AlwaysActiveText?.value}</span>
                            )}
                          </div>
                        </label>
                      )
                    );
                  })}
                </div>
              )}
              {/* manage window cta buttons */}
              <div className="flex flex-col gap-2 py-4">
                {ConfirmOptionsButtonText?.value && (
                  <DGAButton
                    size="md"
                    variant="primary"
                    className="!w-full"
                    onClick={() => handleConfirmOptions()}
                    buttonContent={ConfirmOptionsButtonText?.value}
                  />
                )}
                {RejectAllButtonText?.value && (
                  <DGAButton
                    size="md"
                    variant="secondaryOutline"
                    className="!w-full"
                    buttonContent={RejectAllButtonText?.value}
                    onClick={() => RejectAll()}
                  />
                )}
              </div>
            </div>
          )}
          {/* success window */}
          {showSuccessWindow && (
            <div className="flex flex-col gap-4">
              {ResponseMessage?.value && <RichText field={ResponseMessage} />}
              {/* success cta */}
              {UndoButtonText?.value && (
                <DGAButton
                  size="md"
                  variant="secondarySolid"
                  className="!w-full"
                  onClick={() => handleUndo()}
                  buttonContent={UndoButtonText?.value}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookiesBanner;
